import { Phone, Mail, Globe, Clock, MapPin } from "lucide-react";
import Page from "@/components/Page";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { useContent } from "@/content/ContentContext";

const ICONS = [Clock, MapPin, Globe];

const Contact = () => {
  const { content } = useContent();
  const { site, contact } = content;
  return (
    <Page data-testid="contact-page">
      <PageHero
        id="contact"
        overline={contact.heroOverline}
        lines={contact.heroLines}
        intro={contact.heroIntro}
      />

      <section className="bg-cream" data-testid="contact-details">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <div className="grid gap-5 lg:grid-cols-2">
            <Reveal>
              <a
                href={`tel:${site.phoneHref}`}
                data-testid="contact-phone-card"
                className="group relative block overflow-hidden border border-line bg-pine text-cream grain px-8 py-12 sm:px-12 sm:py-14 h-full"
              >
                <div className="relative z-10">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 text-gold transition-transform duration-300 group-hover:-translate-y-1">
                    <Phone size={20} />
                  </span>
                  <p className="mt-8 text-xs font-semibold tracking-[0.32em] uppercase text-gold">{contact.phoneTitle}</p>
                  <p className="mt-3 font-serif text-4xl sm:text-5xl font-medium transition-colors duration-300 group-hover:text-gold">
                    {site.phone}
                  </p>
                  <p className="mt-4 text-sm text-cream/60 leading-relaxed">
                    {contact.phoneNote}
                  </p>
                </div>
              </a>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href={`mailto:${site.email}`}
                data-testid="contact-email-card"
                className="group relative block overflow-hidden border border-line bg-white px-8 py-12 sm:px-12 sm:py-14 h-full"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-pine/30 text-pine transition-transform duration-300 group-hover:-translate-y-1">
                  <Mail size={20} />
                </span>
                <p className="mt-8 text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted">{contact.emailTitle}</p>
                <p className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-pine break-all transition-colors duration-300 group-hover:text-gold-muted">
                  {site.email}
                </p>
                <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                  {contact.emailNote}
                </p>
              </a>
            </Reveal>
          </div>

          <div className="mt-5 grid gap-px bg-line border border-line sm:grid-cols-3">
            {contact.notes.map((n, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={0.08}>
                  <div className="h-full bg-white px-8 py-9" data-testid={`contact-note-${i + 1}`}>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-pine text-gold">
                      <Icon size={16} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl font-medium text-pine">{n.t}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{n.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative bg-pine text-cream grain" data-testid="contact-closing">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 text-left">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold mb-6">{contact.quoteOverline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.1] max-w-3xl">
              &ldquo;{contact.quote}&rdquo;
            </h2>
            <p className="mt-8 max-w-xl text-base text-cream/70 leading-relaxed">
              {contact.quoteText}
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee />
    </Page>
  );
};

export default Contact;
